import React from "react";
import { Tooltip, Switch } from "antd";

const permissionLabels = {
  View: "Ver",
  Create: "Crear",
  Update: "Modificar",
  Delete: "Eliminar",
  Unassign: "Desasignar",
  Assign: "Asignar"
};

export const getColumns = (
  dataSource,
  setDataSource,
  selectedView,
  permissionsByView
) => {
  const viewPermissions = permissionsByView[selectedView] || [];

  const allPermissionChecked = (permission) => {
    return dataSource.every(
      (row) => row.permissions?.[selectedView]?.[permission] === true
    );
  };

  const propagateAssignmentView = (row) => {
    // Activar View en assets, dependencies y controls
    if (row.permissions["assets"]) {
      row.permissions["assets"]["View"] = true;
    }
    if (row.permissions["dependencies"]) {
      row.permissions["dependencies"]["View"] = true;
    }
    if (row.permissions["controls"]) {
      row.permissions["controls"]["View"] = true;
    }
  };

  const propagateAllAssignmentViews = () => {
    // Activar View en assets, dependencies y controls para todas las filas
    const newData = dataSource.map((row) => {
      if (row.permissions["assets"]) {
        row.permissions["assets"]["View"] = true;
      }
      if (row.permissions["dependencies"]) {
        row.permissions["dependencies"]["View"] = true;
      }
      if (row.permissions["controls"]) {
        row.permissions["controls"]["View"] = true;
      }
      return row;
    });
    setDataSource(newData);
  };

  const toggleAllPermission = (permission, checked) => {
    const newData = dataSource.map((row) => {
      const updatedPerms = {
        ...row.permissions[selectedView],
        [permission]: checked,
      };

      if (permission !== "View" && checked) {
        updatedPerms["View"] = true;
      }

      if (permission === "View" && !checked) {
        viewPermissions.forEach((perm) => {
          updatedPerms[perm] = false;
        });
      }

      return {
        ...row,
        permissions: {
          ...row.permissions,
          [selectedView]: updatedPerms,
        },
      };
    });

    // 🆕 REGLA: Si se activa "View" en "assignment", activar View en assets, dependencies y controls
    if (selectedView === "assignment" && permission === "View" && checked) {
      propagateAllAssignmentViews();
    }

    // 🆕 REGLA: Si se activa CUALQUIER permiso (Update, Create, Delete) en "assignment", 
    // activar View en assets, dependencies y controls (porque también se activa View automáticamente)
    if (selectedView === "assignment" && permission !== "View" && checked) {
      propagateAllAssignmentViews();
    }

    if (selectedView === "dependencies" && checked) {
      newData.forEach((row) => {
        if (row.permissions["assets"]) {
          row.permissions["assets"]["View"] = true;
        }
      });
    }

    setDataSource(newData);
  };

  const toggleAllPermissionsRow = (index, checked) => {
    const newData = [...dataSource];
    const row = newData[index];

    if (!row.permissions[selectedView]) {
      row.permissions[selectedView] = {};
    }

    viewPermissions.forEach((perm) => {
      row.permissions[selectedView][perm] = checked;
    });

    if (selectedView === "assignment" && checked) {
      propagateAssignmentView(row);
    }

    if (selectedView === "dependencies" && checked) {
      if (row.permissions["assets"]) {
        row.permissions["assets"]["View"] = true;
      }
    }

    setDataSource(newData);
  };

  return [
    {
      title: () => (
        <div style={{ textAlign: "center", width: "100%" }}>Areas</div>
      ),
      dataIndex: "area",
      key: "area",
      fixed: "left",
      width: "25%",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          backgroundColor: "#E0F8AD",
          color: "#006939",
          fontWeight: "bold",
        },
      }),
    },
    {
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#E0F8AD",
          color: "#006939",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      children: viewPermissions.map((permission) => ({
        title: (
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <div>{permissionLabels[permission] || permission}</div>
            <Tooltip title="Asignar todos" placement="top">
              <Switch
                className="custom-assignment-switch"
                checked={allPermissionChecked(permission)}
                onChange={(checked) =>
                  toggleAllPermission(permission, checked)
                }
                style={{ marginTop: 4 }}
                size="small"
              />
            </Tooltip>
          </div>
        ),
        dataIndex: ["permissions", selectedView, permission],
        key: `${selectedView}-${permission}`,
        width: 100,
        onHeaderCell: () => ({
          style: {
            backgroundColor: "#E0F8AD",
            color: "#006939",
            fontWeight: "bold",
            textAlign: "center",
          },
        }),
        render: (_, record, index) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Switch
              className="custom-assignment-switch"
              checked={record.permissions[selectedView][permission]}
              onChange={(checked) => {
                const newData = [...dataSource];
                newData[index].permissions[selectedView][permission] = checked;

                if (permission !== "View" && checked) {
                  newData[index].permissions[selectedView]["View"] = true;

                  if (selectedView === "assignment") {
                    propagateAssignmentView(newData[index]);
                  }
                }

                if (permission === "View" && !checked) {
                  viewPermissions.forEach((perm) => {
                    newData[index].permissions[selectedView][perm] = false;
                  });
                }

                if (selectedView === "assignment" && permission === "View" && checked) {
                  propagateAssignmentView(newData[index]);
                }
                if (selectedView === "assignment" && permission !== "View" && checked) {
                  propagateAssignmentView(newData[index]);
                }
                if (selectedView === "dependencies" && checked) {
                  if (newData[index].permissions["assets"]) {
                    newData[index].permissions["assets"]["View"] = true;
                  }
                }

                setDataSource(newData);
              }}
            />
          </div>
        ),
      })),
    },
    {
      key: "assignAllRow",
      width: "5%",
      fixed: "right",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#E0F8AD",
          color: "#006939",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (_, record, index) => {
        const allChecked = viewPermissions.every(
          (perm) => record.permissions[selectedView][perm] === true
        );

        return (
          <Tooltip title="Asignar todos">
            <Switch
              className="custom-assignment-switch"
              checked={allChecked}
              onChange={(checked) => toggleAllPermissionsRow(index, checked)}
              size="small"
            />
          </Tooltip>
        );
      },
    },
  ];
};
